/**
 * La marca, en sus tres formas.
 *
 * ## Por qué son imágenes y no texto
 *
 * Hasta acá el nombre del producto era un `<span>` con una tipografía cualquiera. Un logotipo es
 * una forma dibujada: sus proporciones, el corte de la «A» y el punto del signo de admiración no
 * se reproducen con `font-weight: 600`, y aproximarlos se nota.
 *
 * ## Por qué el `alt` cambia según el caso
 *
 * El isotipo suelto **no lleva texto alternativo** cuando aparece al lado del nombre escrito:
 * repetirlo hace que un lector de pantalla diga «ActualGold ActualGold». Cuando va solo, sí.
 */

type Forma = 'iso' | 'logotipo' | 'completo';

/**
 * Dos juegos: tinta oscura para fondos claros, tinta clara para fondos oscuros.
 *
 * El rojo es el mismo en los dos —es la marca— y lo que cambia es el negro. Un solo archivo no
 * alcanza: el logotipo original viene sobre blanco, y sobre el panel negro del acceso se veía como
 * un recuadro blanco pegado encima.
 */
const ARCHIVOS: Record<'oscura' | 'clara', Record<Forma, string>> = {
  oscura: {
    iso: '/marca/iso.png',
    logotipo: '/marca/logo.png',
    completo: '/marca/logo_iso.png',
  },
  clara: {
    iso: '/marca/iso-claro.png',
    logotipo: '/marca/logo-claro.png',
    completo: '/marca/logo_iso-claro.png',
  },
};

export function Marca({
  forma = 'logotipo',
  className = '',
  decorativa = false,
  tinta = 'oscura',
}: {
  readonly forma?: Forma;
  readonly className?: string;
  /** Cuando el nombre ya está escrito al lado y la imagen sólo lo acompaña. */
  readonly decorativa?: boolean;
  readonly tinta?: 'oscura' | 'clara';
}): React.ReactElement {
  return (
    <img
      src={ARCHIVOS[tinta][forma]}
      alt={decorativa ? '' : 'ActualGold!'}
      aria-hidden={decorativa}
      className={className}
      // Sin esto el logo salta de tamaño mientras carga y empuja la barra entera.
      decoding="async"
    />
  );
}

/**
 * La marca que se adapta al tema.
 *
 * Dibuja las dos tintas y esconde una con CSS, en vez de elegir en JavaScript. Es a propósito: el
 * tema se aplica con una clase en la raíz —puede cambiar sin que este componente vuelva a
 * dibujarse—, y una marca que se queda en la tinta anterior es exactamente lo que pasaba en modo
 * oscuro: el «Actual» negro sobre la barra negra, ilegible.
 */
export function MarcaAdaptable({
  forma = 'logotipo',
  className = '',
}: {
  readonly forma?: Forma;
  readonly className?: string;
}): React.ReactElement {
  return (
    <>
      <Marca forma={forma} className={`${className} oscuro:hidden`} />
      <Marca
        forma={forma}
        tinta="clara"
        decorativa
        className={`hidden ${className} oscuro:block`}
      />
    </>
  );
}

/**
 * El sello de quien hace el producto.
 *
 * Va abajo del acceso y en ningún otro lado: es de quien lo fabrica, no de quien lo usa, y un
 * comercio que entra ocho veces por día no necesita que se lo recuerden ocho veces.
 */
export function SelloDeActualSoft({
  className = '',
  tinta = 'oscura',
}: {
  readonly className?: string;
  readonly tinta?: 'oscura' | 'clara';
}): React.ReactElement {
  return (
    <img
      src={tinta === 'clara' ? '/marca/logo_iso_empresa-claro.png' : '/marca/logo_iso_empresa.png'}
      alt="ActualSoft!"
      className={className}
      decoding="async"
    />
  );
}
